import { concat, fn, hash } from '@ember/helper';
import { get, action } from '@ember/object';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvBtn from 'volta/components/bv-btn';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import tElse from 'volta/helpers/t-else';

import type { WithBoundArgs } from '@glint/template';
import type BvBtnComponent from 'volta/components/bv-btn';
import type { IBtnColor, TButtonSize } from 'volta/components/bv-btn';
import type { TTooltipSide } from 'volta/components/bv-tooltip';

export interface IBvBtnGroupContent {
  iconLeft?: string;
  iconRight?: string;
  tooltipText?: string;
  disabled?: boolean;
  action?: () => void;
  hidden?: boolean;
  key?: string;
  label?: string;
}

interface BvBtnGroupSignature {
  Args: {
    color?: IBtnColor;
    size?: TButtonSize;
    activeValue?: string;
    content?: IBvBtnGroupContent[];
    labelPath?: string;
    keyPath?: string;
    nullable?: boolean;
    outline?: boolean;
    disabled?: boolean;
    subtle?: boolean;
    plain?: boolean;
    rounded?: boolean;
    fullWidth?: boolean;
    isLoading?: boolean;
    justified?: boolean;
    segmented?: boolean;
    tooltipSide?: TTooltipSide;
    onChange?: (value?: string | null) => void;
  };
  Blocks: {
    default: [
      {
        btn: WithBoundArgs<
          typeof BvBtnComponent,
          | 'outline'
          | 'color'
          | 'size'
          | 'disabled'
          | 'rounded'
          | 'subtle'
          | 'plain'
          | 'fullWidth'
          | 'tooltipSide'
          | 'isLoading'
        >;
      }
    ];
  };
  Element: HTMLDivElement;
}

export default class BvBtnGroupComponent extends Component<BvBtnGroupSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked activeValue?: string | null = this.args.activeValue;

  segmented = true;

  get content() {
    return this.args.content ?? [];
  }

  get labelPath() {
    return this.args.labelPath ?? 'label';
  }

  get keyPath() {
    return this.args.keyPath ?? 'key';
  }

  @action
  handleDidUpdate(_el: HTMLElement, [key]: [string | undefined]) {
    this.setActiveValue(key);
  }

  @action
  handleChange(key?: string) {
    if (key == null) {
      return;
    }
    this.setActiveValue(key);
    this.args.onChange?.(this.activeValue);
  }

  @action
  handleClick(evt: InputEvent) {
    evt.stopPropagation();
  }

  setActiveValue(value?: string) {
    if (this.activeValue !== value) {
      const { nullable, activeValue } = this.args;
      const shouldBeNull = nullable && activeValue === value;
      this.activeValue = shouldBeNull ? null : value;
    }
  }

  getKey = (row: IBvBtnGroupContent) => {
    const key = get(row, this.keyPath);
    if (typeof key === 'string') {
      return key;
    }
    return undefined;
  };

  getLabel = (row: IBvBtnGroupContent) => {
    const label = get(row, this.labelPath);
    if (typeof label === 'string') {
      return label;
    }
    return undefined;
  };

  <template>
    {{! template-lint-disable no-at-ember-render-modifiers }}
    <div
      class={{classNames
        'bv-btn-group'
        (if @disabled 'disabled')
        (if (orElse @segmented true) 'bv-btn-group--segmented')
        (if @justified 'bv-btn-group--justified')
      }}
      disabled={{@disabled}}
      role='group'
      aria-label='...'
      {{didUpdate this.handleDidUpdate @activeValue}}
      ...attributes
    >
      {{#if (and (not (has-block)) this.content.length)}}
        {{#each this.content key=this.keyPath as |row|}}
          {{#if (and row (not row.hidden))}}
            {{#let (this.getKey row) (this.getLabel row) as |_key _label|}}
              <BvBtn
                @outline={{@outline}}
                @color={{@color}}
                @subtle={{@subtle}}
                @plain={{@plain}}
                @size={{@size}}
                @iconLeft={{row.iconLeft}}
                @iconRight={{row.iconRight}}
                @tooltipText={{row.tooltipText}}
                @pressed={{eq this.activeValue _key}}
                @onClick={{if row.action row.action (fn this.handleChange _key)}}
                @disabled={{or @disabled row.disabled}}
                @fullWidth={{@fullWidth}}
                @text={{tElse _label}}
                @isLoading={{@isLoading}}
                @testName={{concat 'group-' _key}}
              />
            {{/let}}
          {{/if}}
        {{/each}}
      {{else}}
        {{yield
          (hash
            btn=(component
              BvBtn
              outline=@outline
              color=@color
              size=@size
              disabled=@disabled
              rounded=@rounded
              subtle=@subtle
              plain=@plain
              fullWidth=@fullWidth
              tooltipSide=@tooltipSide
              isLoading=@isLoading
            )
          )
        }}
      {{/if}}
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvBtnGroup: typeof BvBtnGroupComponent;
    'bv-btn-group': typeof BvBtnGroupComponent;
  }
}
